import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { projectsAPI } from '../services/api';
import { 
  Building2, 
  Plus, 
  Search, 
  MapPin, 
  Calendar, 
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const Projects = () => {
  const { isDeveloper } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [searchTerm, statusFilter, projects]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const projects = response?.data?.data || response?.data || [];
      setProjects(projects);
      setFilteredProjects(projects);
    } catch (error) {
      console.error('Error al cargar obras:', error);
      toast.error('Error al cargar obras');
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];
    
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    setFilteredProjects(filtered);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await projectsAPI.delete(deleteId);
      toast.success('Obra eliminada');
      fetchProjects();
    } catch (error) {
      toast.error('Error al eliminar obra');
    } finally {
      setDeleteId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      activo: 'bg-[#C05621]/10 text-[#C05621] hover:bg-[#C05621]/20',
      completado: 'bg-green-100 text-green-800 hover:bg-green-200',
      pausado: 'bg-amber-100 text-amber-800 hover:bg-amber-200'
    };
    return styles[status] || styles.activo;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-48 rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">
              {isDeveloper() ? 'Mis Obras' : 'Obras Asignadas'}
            </h1>
            <p className="text-[#5C554B]">{projects.length} obras en total</p>
          </div>
          {isDeveloper() && (
            <Link to="/projects/new">
              <Button className="btn-primary" data-testid="new-project-btn">
                <Plus className="w-5 h-5 mr-2" />
                Nueva Obra
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
            <Input
              placeholder="Buscar por nombre o ubicación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-default pl-12"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 h-12 bg-white border-[#E2E8F0]">
              <Filter className="w-4 h-4 mr-2 text-[#5C554B]" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="activo">Activo</SelectItem>
              <SelectItem value="pausado">Pausado</SelectItem>
              <SelectItem value="completado">Completado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length === 0 ? (
          <Card className="card-dashboard">
            <CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#2D2A26] mb-2">
                {searchTerm || statusFilter !== 'all' 
                  ? 'No se encontraron obras'
                  : isDeveloper() 
                    ? 'No tienes obras creadas'
                    : 'No tienes obras asignadas'
                }
              </h3>
              <p className="text-[#5C554B] mb-6">
                {searchTerm || statusFilter !== 'all'
                  ? 'Intenta con otros filtros'
                  : isDeveloper()
                    ? 'Crea tu primera obra para comenzar'
                    : 'Espera a que un desarrollador te asigne una obra'
                }
              </p>
              {isDeveloper() && !searchTerm && statusFilter === 'all' && (
                <Link to="/projects/new">
                  <Button className="btn-primary">
                    <Plus className="w-5 h-5 mr-2" />
                    Crear obra
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project, index) => (
              <Card 
                key={project.id} 
                className="card-dashboard group animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CardContent className="p-0">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#C05621]/10 rounded-xl flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-[#C05621]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#2D2A26] line-clamp-1">{project.name}</h3>
                          <Badge className={getStatusBadge(project.status)}>
                            {project.status}
                          </Badge>
                        </div>
                      </div>
                      {isDeveloper() && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver detalle
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}/edit`)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setDeleteId(project.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    {project.location && (
                      <div className="flex items-center gap-2 text-sm text-[#5C554B] mb-2">
                        <MapPin className="w-4 h-4" />
                        <span className="line-clamp-1">{project.location}</span>
                      </div>
                    )}

                    {project.start_date && (
                      <div className="flex items-center gap-2 text-sm text-[#5C554B] mb-4">
                        <Calendar className="w-4 h-4" />
                        <span>{project.start_date} - {project.end_date || 'En curso'}</span>
                      </div>
                    )}

                    <div className="pt-4 border-t border-[#E2E8F0]">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-[#5C554B]">Presupuesto</p>
                          <p className="font-semibold text-[#2D2A26]">{formatCurrency(project.budget)}</p>
                        </div>
                        <Link to={`/projects/${project.id}`}>
                          <Button variant="ghost" size="sm" className="text-[#C05621]" data-testid={`view-project-${project.id}`}>
                            Ver más
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta obra?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán todos los datos asociados a la obra.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Projects;
